import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Form, Input} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Collapse} from 'react-collapse';
import {
  StyledCompanyInformationForm,
  StyledCompanyInformationFormAlert
} from './StyledCompanyInformationForm';
import {StyledKYCModalContentStepTitle} from '../../../../KYCModal/StyledKYCModal';
import SpinSmall from '../../../../../SpinSmall';
import AntdCountriesSelect from '../../../../../Select/AntdCountriesSelect';
import {companyActions} from '../../../../../../state/actions/company.actions';
import {userActions} from '../../../../../../state/actions/user.actions';
import {useEmployeeEmail, useLocation} from '../../../../../../hooks';
import {
  formHelpers,
  kycHelpers,
  localizationHelpers,
  objectHelpers
} from '../../../../../../utils/helpers';

const {Item} = Form;

const supportedCountries = localizationHelpers.getSupportedCountries();

const countrySelectProps = kycHelpers.getCountrySelectProps();

/**
 * Name, country and email — and deliberately nothing else (H1MVP-4652).
 *
 * A WithLess rep fills this in while on a call with the client, so every field here is a question
 * they have to ask out loud. The logo and the currency were dropped for that reason rather than to
 * save space: nobody knows their brand asset URL mid-call, and the currency is not a decision the
 * client should be making at all — the backend derives it from the country.
 *
 * These three are also exactly what the endpoint requires. `POST /v2/companies/registration` with an
 * empty body answers `company_name`, `country`, `email`, and does NOT ask for a currency, so the
 * form and the contract agree by construction.
 */
const initialFormValues = {
  country: undefined,
  email: '',
  name: ''
}

const checkIfSupportedCountry = (country) => {
  let supported = false;
  if (country) {
    const countryObj = supportedCountries.find(c => c.name === country);
    if (countryObj) supported = kycHelpers.isSupportedCountry(countryObj['ISO3166-1-Alpha-2']);
  } else {
    supported = true;
  }
  return supported;
}

const CompanyInformationForm = React.forwardRef(({
  onSubmit,
  ...rest
}, ref) => {
  const dispatch = useDispatch();
  const employeeEmail = useEmployeeEmail();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [t] = useTranslation(['main', 'quickStart']);
  const [form] = Form.useForm();
  const {name: defaultCountryName} = useLocation();

  const country = Form.useWatch('country', form);

  const isSupported = useMemo(() => checkIfSupportedCountry(country), [country]);

  const {
    info,
    label,
    lengthFieldMessage,
    placeholder,
    validationEmailRules,
    validationRequiredRules,
    title
  } = useMemo(() => kycHelpers.getKYCFormDetails({t, formKey: 'company'}), [t]);

  useEffect(() => {
    if (defaultCountryName && !country) {
      form.setFieldValue('country', defaultCountryName);
    }
  }, [defaultCountryName, form]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishLoading = () => setLoading(false);

  const onFinish = (fields) => {
    const data = {
      company_name: fields.name,
      country: fields.country,
      email: fields.email
    };
    setLoading(true);
    setSubmitError(null);
    dispatch(companyActions.companyRegistration(
      data,
      (response) => {
        const companyId = objectHelpers.getObjProp(response, 'company_id')
        dispatch(userActions.getCompanies(
          null,
          () => {
            dispatch(userActions.changeCompany(companyId));
            finishLoading();
            onSubmit && onSubmit({enabledKyc: isSupported, companyId, companyName: fields.name});
            form.setFieldsValue({
              ...initialFormValues,
              country: defaultCountryName
            });
          },
          finishLoading,
        ));
      },
      (err) => {
        const errorsData = err.response?.data || {};
        const fieldErrors = formHelpers.getFormServerErrorFields(errorsData);
        form.setFields(fieldErrors);
        // Not every failure belongs to a field. A staff-gated 403 answers
        // `{"message": "Requires backoffice-support access"}` with nothing to attach to an input, so
        // without this the drawer just stopped spinning and looked like nothing had happened.
        //
        // ⚠ Deliberately not `formHelpers.handleFormErrors`: that reads field errors from
        // `response.data.data` and the message off the axios response, while this backend puts both
        // at the body root (`{errors: {...}, message: '...'}`). Swapping it in would silently drop
        // both halves rather than fix anything.
        if (fieldErrors.length === 0) {
          setSubmitError(errorsData.message || t('serverErrorMessage'));
        }
        finishLoading();
      }
    ));
  }

  return (
    <StyledCompanyInformationForm
      direction='vertical'
      size={32}
      {...rest}
    >
      <StyledKYCModalContentStepTitle>
        {title}
      </StyledKYCModalContentStepTitle>
      <SpinSmall spinning={loading}>
        <Form
          initialValues={initialFormValues}
          form={form}
          layout='vertical'
          ref={ref}
          onFinish={onFinish}
          requiredMark={false}
        >
          <Item
            label={label('name')}
            name='name'
            rules={[
              ...validationRequiredRules,
              {min: 2, message: lengthFieldMessage(2, 'min')}
            ]}
          >
            <Input
              minLength={2}
              maxLength={50}
              placeholder={placeholder('name')}
              size='large'
            />
          </Item>
          <Item
            label={label('email')}
            name='email'
            rules={[
              ...validationEmailRules,
              () => ({
                validator(_, value) {
                  if (value === employeeEmail) {
                    return Promise.reject(new Error(t('validation.enterDifferentEmailForCompany')));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input
              placeholder={placeholder('email')}
              size='large'
              type='email'
            />
          </Item>
          <Item
            label={label('country')}
            name='country'
            rules={validationRequiredRules}
          >
            <AntdCountriesSelect
              countryPropName='name'
              placeholder={placeholder('country')}
              {...countrySelectProps}
            />
          </Item>
          <Collapse isOpened={!isSupported}>
            <StyledCompanyInformationFormAlert
              align='start'
              size='small'
            >
              <InfoCircleOutlined />
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <p>{info('bankingOnlyAvailable')} {info('applyForWithLess')} {t('please')} <a href='#'>{t('continueHere')}</a></p>
            </StyledCompanyInformationFormAlert>
          </Collapse>
        </Form>
        {submitError && (
          <Alert
            message={submitError}
            showIcon
            type='error'
          />
        )}
      </SpinSmall>
    </StyledCompanyInformationForm>
  );
});

CompanyInformationForm.propTypes = {
  onSubmit: PropTypes.func
}

export default CompanyInformationForm;
